import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPencil,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { API_URL } from "../../config";
import LoadingSpinner from "../../components/LoadingSpinner";
import { PermissionWrapper } from "../../helpers/permission.js";

const AiCallingPrompts = () => {
  const navigate = useNavigate();
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(false);
  const token = localStorage.getItem("authToken");

  // Search filter
  const [titleFilter, setTitleFilter] = useState("");

  // Pagination
  const [page, setPage] = useState(1);
  const limit = 100;

  useEffect(() => {
    fetchPrompts();
  }, []);

  const fetchPrompts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}settings/ai-prompts`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      setPrompts(response.data.data || []);
    } catch (error) {
      console.error("Error fetching AI prompts:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredPrompts = useMemo(() => {
    return prompts.filter((p) => {
      if (titleFilter && !(p.title || "").toLowerCase().includes(titleFilter.toLowerCase())) {
        return false;
      }
      return true;
    });
  }, [prompts, titleFilter]);

  const total = filteredPrompts.length;
  const pageCount = Math.ceil(total / limit);
  const start = (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);
  const paginatedPrompts = filteredPrompts.slice(start - 1, end);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pageCount) setPage(newPage);
  };

  return (
    <div id="page-container" className="page-header-fixed page-sidebar-fixed fade in">
      <div id="content" className="">
        <div className="row pb-2">
          <div className="col-md-6">
            <h4 className="panel-title pull-left">Manage AI Prompts</h4>
          </div>
          <div className="col-md-6" style={{ textAlign: "right" }}>
            {total > 0 && (
              <div className="d-inline-block">
                <span style={{ fontSize: "0.8rem" }} className="me-2">
                  {start} to {end} of {total}
                </span>
                <button
                  className="btn btn-default btn-sm"
                  title="Previous"
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page === 1}
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                </button>
                &nbsp;
                <button
                  className="btn btn-default btn-sm"
                  title="Next"
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page === pageCount}
                >
                  <FontAwesomeIcon icon={faChevronRight} />
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="panel panel-default with-rounded-corner with-shadow">
          {loading ? (
            <span></span>
          ) : (
            <>
              <div style={{ maxHeight: "calc(100vh - 165px)", overflow: "auto" }}>
                {prompts.length > 0 ? (
                  <table className="table m-b-0">
                    <thead>
                      <tr>
                        <th style={{ textAlign: "center", width: "50px" }}>#</th>
                        <th>Title</th>
                        <th>Prompt Text</th>
                        <th>Created On</th>
                        <th style={{ width: "80px" }}>Action</th>
                      </tr>
                      <tr>
                        <th></th>
                        <th>
                          <input
                            type="text"
                            className="form-control input-sm"
                            placeholder="Search title..."
                            value={titleFilter}
                            onChange={(e) => setTitleFilter(e.target.value)}
                          />
                        </th>
                        <th></th>
                        <th></th>
                        <th>
                          <button
                            className="btn btn-default btn-sm"
                            onClick={() => setTitleFilter("")}
                          >
                            Clear
                          </button>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        Array.from({ length: 5 }).map((_, rowIndex) => (
                          <tr key={rowIndex} className="skeleton-row">
                            {Array.from({ length: 5 }).map((_, colIndex) => (
                              <td key={colIndex}>
                                <div className="skeleton-box"></div>
                              </td>
                            ))}
                          </tr>
                        ))
                      ) : paginatedPrompts.length > 0 ? (
                        paginatedPrompts.map((prompt, index) => (
                          <tr key={prompt.id}>
                            <td style={{ textAlign: "center" }}>
                              {(page - 1) * limit + index + 1}
                            </td>
                            <td>{prompt.title}</td>
                            <td
                              style={{
                                maxWidth: "500px",
                                wordWrap: "break-word",
                                whiteSpace: "normal",
                              }}
                            >
                              {prompt.prompt_text}
                            </td>
                            <td>
                              {prompt.created_on
                                ? new Date(prompt.created_on).toLocaleDateString()
                                : "-"}
                            </td>
                            <td>
                              <div className="d-flex gap-2">
                                <PermissionWrapper module="settings_ai_prompts" action="edit">
                                  <Link
                                    className="btn btn-warning btn-xs"
                                    to={`/Settings/AiCallingPrompts/edit/${prompt.id}`}
                                    title="Edit"
                                  >
                                    <FontAwesomeIcon icon={faPencil} />
                                  </Link>
                                </PermissionWrapper>
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="5" className="text-center">
                            No prompts match your search criteria.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                ) : (
                  <div className="alert alert-warning m-3">
                    No AI prompts found.
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AiCallingPrompts;
